import { parseTenantDateTime } from '@utils/timeZone';
import { Emptied } from '@utils/types';
import { ScheduleDialogProps, ScheduleDialogType } from './types';
import { Schedule } from '@root/globalTypes';
import { getAction } from '@components/job/workProgress/helpers';
import { ReactNode } from 'react';
import { SchedulerProvider } from '@contexts/scheduler';
import InternalScheduleDialog from '@components/schedules/dialogs/internal';
import MobileInternalScheduleDialog from '@components/schedules/dialogs/internal/mobile';
import { useIsPhone } from '@hooks/useIsPhone';
import {
  getScheduleDialogType,
  getTaskStageTasks,
  parseInitialParams,
  parseSchedule,
} from './helpers';
import { JOB_STAGES_HINT_STALE_TIME, useGetJobStages } from '@services/jobs/stages';
import ExternalScheduleGenericDialog from '@components/schedules/dialogs/external/generic';
import ExternalScheduleOrderDialog from '@components/schedules/dialogs/external/order';
import FinalYardsDialog from '@components/job/materials/dialogs/finalYards';
import { useAccess } from '@hooks/access';
import { SchedulerSidebarProvider } from '@contexts/scheduler/sidebar';

export const ScheduleDialog = ({
  open,
  setOpen,
  initialDate,
  job,
  task,
  tasks = [],
  initialParams = {},
  phoneLayout,
  takenDays,
}: ScheduleDialogProps) => {
  const access = useAccess();
  const isPhone = useIsPhone();
  const InternalDialog =
    phoneLayout && isPhone ? MobileInternalScheduleDialog : InternalScheduleDialog;

  const { id: jobId, address } = job;
  const geolocalization = [job?.lat, job?.lon].join(', ') || '';

  const {
    id: taskId,
    name,
    scheduleType,
    taskType,
    dateConfirmation,
    concreteDirectOrderExternalId,
    note,
    extraComment,
    schedule,
    foremanSpecialty,
    material,
    forceCompleted,
    status,
  } = task;

  const { id, startsAt }: Emptied<Schedule> = schedule || {};
  const dialogType = getScheduleDialogType(scheduleType, taskType);
  const needsStageTasks = open && !tasks.length && ['external', 'order'].includes(dialogType);
  const stages = useGetJobStages({
    projectId: job.projectId,
    jobId,
    enabled: needsStageTasks,
    staleTime: JOB_STAGES_HINT_STALE_TIME,
  });
  const stageTasks = tasks.length ? tasks : getTaskStageTasks(stages.data?.records, taskId);
  const importantTasks = stageTasks.flatMap((el) => el.tasks).filter((el) => el.important);
  const action = getAction(task);
  const isNotRevertable = status === 'completed' && !forceCompleted;

  const stageImportantTaskStartsAt =
    tasks
      .find((el) => el.date === initialDate)
      ?.tasks?.find((el) => el.importantSchedule && el.schedule?.startsAt)?.schedule?.startsAt ||
    '';

  const getInternalInitialDate = () => {
    let date = startsAt;
    if (initialDate) date = initialDate;
    if (action === 'schedule') date = stageImportantTaskStartsAt;
    return date ? parseTenantDateTime(date) : undefined;
  };

  const getExternalInitialDate = () => {
    let date = startsAt;
    if (!date) date = initialDate || '';
    return { startsAt: date };
  };

  const dialogs: Record<ScheduleDialogType, ReactNode> = {
    internal: (
      <SchedulerProvider>
        <SchedulerSidebarProvider>
          <InternalDialog
            open={open}
            setOpen={setOpen}
            type={foremanSpecialty}
            jobId={jobId}
            schedule={parseSchedule(job, task)}
            initialDate={getInternalInitialDate()}
            activeId={id}
            readOnly={access.readOnly}
            initialParams={parseInitialParams(initialParams, foremanSpecialty?.value)}
          />
        </SchedulerSidebarProvider>
      </SchedulerProvider>
    ),
    external: (
      <ExternalScheduleGenericDialog
        importantTasks={importantTasks}
        open={open}
        name={name}
        setOpen={setOpen}
        jobId={jobId}
        taskId={taskId}
        schedule={{
          ...parseSchedule(job, task),
          ...getExternalInitialDate(),
        }}
        completed={isNotRevertable}
        note={note}
        extraComment={extraComment}
        readOnly={access.readOnly}
        scheduleType="external"
        phoneLayout={phoneLayout && isPhone}
        takenDays={takenDays}
      />
    ),
    order: (
      <ExternalScheduleOrderDialog
        concrete={material}
        importantTasks={importantTasks}
        open={open}
        setOpen={setOpen}
        completed={isNotRevertable}
        jobId={jobId}
        taskId={taskId}
        name={name}
        schedule={{
          ...parseSchedule(job, task),
          ...getExternalInitialDate(),
        }}
        note={note}
        concreteDirectId={concreteDirectOrderExternalId}
        address={address}
        geolocalization={geolocalization}
        additionalInputs={dateConfirmation === 'external'}
        extraComment={extraComment}
        readOnly={access.readOnly}
        scheduleType={scheduleType}
        phoneLayout={phoneLayout && isPhone}
        takenDays={takenDays}
      />
    ),
    finalYards: (
      <FinalYardsDialog
        open={open}
        setOpen={setOpen}
        initialValues={material}
        jobId={jobId}
        taskId={taskId}
        completed={isNotRevertable}
        schedule={parseSchedule(job, task)}
        final
        readOnly={access.readOnly}
        scheduleType={scheduleType}
      />
    ),
  };

  return dialogs[dialogType];
};
